import {
	ChevronDownIcon,
	Chip,
	cn,
	Sheet,
	SortIcon,
} from "@au-schein-de/au-ui-library";
import { type KeyboardEvent, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
	PHARMACY_PICKER_FILTER_SHEET_CONTENT_CLASS,
	PHARMACY_PICKER_FILTER_SHEET_Z_CLASS,
	PharmacyPickerFilterSheetHeader,
} from "~/features/products/pharmacy-picker/pharmacy-picker-filter-sheet-header.component";
import {
	type PharmacyPickerSortContext,
	resolvePharmacyPickerSort,
	usePharmacyPickerSort,
} from "~/features/products/pharmacy-picker/pharmacy-picker-sort-preference";
import {
	PHARMACY_PICKER_SORT_LABEL_KEYS,
	PHARMACY_PICKER_SORT_PANEL_TEST_ID,
	PHARMACY_PICKER_SORT_PANEL_WIDTH_PX,
	PharmacyPickerSortOptions,
	PharmacyPickerSortSidePanel,
} from "~/features/products/pharmacy-picker/pharmacy-picker-sort-side-panel.component";
import { usePanelOutsideClose } from "~/hooks/use-panel-outside-close.hook";

/** Figma 4660:59166: the panel opens 12px underneath the chip. */
const SORT_PANEL_GAP_PX = 12;

function activateOnEnterOrSpace(
	event: KeyboardEvent<HTMLElement>,
	action: () => void,
): void {
	if (event.key === "Enter" || event.key === " ") {
		event.preventDefault();
		action();
	}
}

const panelStyleForAnchor = (
	anchor: DOMRect,
	container?: DOMRect,
): { top: number; left: number } => {
	const left = Math.min(
		anchor.left,
		window.innerWidth - PHARMACY_PICKER_SORT_PANEL_WIDTH_PX - SORT_PANEL_GAP_PX,
	);
	const top = anchor.bottom + SORT_PANEL_GAP_PX;
	if (container) {
		return { top: top - container.top, left: left - container.left };
	}
	return { top, left };
};

function pickerContentForAnchor(anchor: HTMLDivElement | null): Element | null {
	return (
		anchor?.closest("[data-testid='pharmacy-picker-panel-slot']") ??
		anchor?.closest("[data-testid='pharmacy-picker-content']") ??
		null
	);
}

function PharmacyPickerSortIcon() {
	return (
		<span aria-hidden="true" className="flex [&_svg_*]:[stroke-width:1.6]">
			<SortIcon size={4} />
		</span>
	);
}

export function PharmacyPickerSortMenu({
	chipClassName = "h-9 shrink-0 cursor-pointer gap-2 rounded-xl bg-gray-200 p-3 text-body-sm font-medium text-gray-700",
	sortContext,
}: {
	chipClassName?: string;
	sortContext: PharmacyPickerSortContext;
}) {
	const { t } = useTranslation(["product", "common"]);
	const [sort, setSort] = usePharmacyPickerSort();
	// The preference is shared across surfaces, hence the resolved value rather
	// than the raw one: a sort this surface does not offer falls back.
	const appliedSort = resolvePharmacyPickerSort(sort, sortContext);
	const [desktopOpen, setDesktopOpen] = useState(false);
	const [sheetOpen, setSheetOpen] = useState(false);
	// Figma "mobile states": select-then-confirm, matching RadiusSelectSheet —
	// unlike the desktop panel, which still applies immediately on tap.
	// The draft starts from the raw preference so Apply without a pick keeps
	// "no preference" and each surface its own default.
	const [draftSort, setDraftSort] = useState(sort);
	const openSheet = () => {
		setDraftSort(sort);
		setSheetOpen(true);
	};
	const applyDraftSort = () => {
		if (draftSort != null) setSort(draftSort);
		setSheetOpen(false);
	};
	const [panelStyle, setPanelStyle] = useState<{ top: number; left: number }>();
	const [pickerContent, setPickerContent] = useState<Element | null>(null);
	const desktopAnchorRef = useRef<HTMLDivElement>(null);
	const label = t("vendor.picker.filter.sort");
	// The chip names the sort that is actually applied, including the default, so
	// the control reports the list's state instead of only inviting a change.
	const chipLabel = t(PHARMACY_PICKER_SORT_LABEL_KEYS[appliedSort]);
	const dockedToPicker = pickerContent != null;

	const toggleDesktopOpen = () => {
		const next = !desktopOpen;
		setDesktopOpen(next);
		setPickerContent(
			next ? pickerContentForAnchor(desktopAnchorRef.current) : null,
		);
	};

	usePanelOutsideClose({
		isOpen: desktopOpen,
		anchorRef: desktopAnchorRef,
		panelTestId: PHARMACY_PICKER_SORT_PANEL_TEST_ID,
		onClose: () => setDesktopOpen(false),
	});

	useLayoutEffect(() => {
		if (!desktopOpen) {
			setPickerContent(null);
			return;
		}
		const picker = pickerContentForAnchor(desktopAnchorRef.current);
		setPickerContent(picker);
		const updatePosition = () => {
			const rect = desktopAnchorRef.current?.getBoundingClientRect();
			if (!rect) return;
			setPanelStyle(panelStyleForAnchor(rect, picker?.getBoundingClientRect()));
		};
		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);
		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [desktopOpen]);

	return (
		<>
			<div className="relative shrink-0 max-md:hidden" ref={desktopAnchorRef}>
				<Chip
					variant="filter"
					className={cn(
						chipClassName,
						"border-2",
						desktopOpen ? "border-ansay-700" : "border-transparent",
					)}
					data-testid="pharmacy-picker-filter-sort"
					// biome-ignore lint/a11y/useSemanticElements: Chip is the visual control and is not a native button
					role="button"
					tabIndex={0}
					onPointerDown={(event) => event.stopPropagation()}
					onClick={toggleDesktopOpen}
					onKeyDown={(event) => {
						activateOnEnterOrSpace(event, toggleDesktopOpen);
					}}
				>
					<PharmacyPickerSortIcon />
					{chipLabel}
					<span className={cn("flex", desktopOpen && "rotate-180")}>
						<ChevronDownIcon size={5} />
					</span>
				</Chip>
			</div>
			{desktopOpen
				? createPortal(
						<PharmacyPickerSortSidePanel
							sortContext={sortContext}
							onClose={() => setDesktopOpen(false)}
							docked={dockedToPicker}
							style={panelStyle}
						/>,
						pickerContent ?? document.body,
					)
				: null}
			<div className="shrink-0 md:hidden">
				<Chip
					variant="filter"
					className={cn(
						chipClassName,
						"border-2",
						sheetOpen ? "border-ansay-700" : "border-transparent",
					)}
					data-testid="pharmacy-picker-filter-sort-mobile"
					// biome-ignore lint/a11y/useSemanticElements: Chip is the visual control and is not a native button
					role="button"
					tabIndex={0}
					onPointerDown={(event) => event.stopPropagation()}
					onClick={openSheet}
					onKeyDown={(event) => {
						activateOnEnterOrSpace(event, openSheet);
					}}
				>
					<PharmacyPickerSortIcon />
					{chipLabel}
					<span className={cn("flex", sheetOpen && "rotate-180")}>
						<ChevronDownIcon size={5} />
					</span>
				</Chip>
				<Sheet.Root
					open={sheetOpen}
					onOpenChange={(nextOpen) => {
						if (nextOpen) {
							setDraftSort(sort);
						}
						setSheetOpen(nextOpen);
					}}
				>
					<Sheet.Portal>
						<Sheet.Overlay className={PHARMACY_PICKER_FILTER_SHEET_Z_CLASS} />
						<Sheet.Content
							side="bottom"
							className={PHARMACY_PICKER_FILTER_SHEET_CONTENT_CLASS}
							data-testid="pharmacy-picker-sort-sheet"
							aria-describedby={undefined}
						>
							<PharmacyPickerFilterSheetHeader
								title={label}
								onClose={() => setSheetOpen(false)}
								closeLabel={t("common:close")}
								closeTestId="pharmacy-picker-sort-sheet-close"
							/>
							<div className="flex flex-col gap-2">
								<PharmacyPickerSortOptions
									sortContext={sortContext}
									selected={resolvePharmacyPickerSort(draftSort, sortContext)}
									onSelect={setDraftSort}
									optionClassName="bg-white"
								/>
							</div>
							<button
								type="button"
								className="mt-6 w-full rounded-xl bg-ansay-light p-4 text-body-emphasis text-gray-700 transition-opacity hover:opacity-90"
								onClick={applyDraftSort}
								data-testid="pharmacy-picker-sort-apply"
							>
								{t("vendor.picker.sort.apply")}
							</button>
						</Sheet.Content>
					</Sheet.Portal>
				</Sheet.Root>
			</div>
		</>
	);
}
